import * as React from 'react';
import { SettingsProperty } from '../../engine/properties/SettingsProperty';
import { BooleanControl } from './controls/BooleanControl';
import { NumberControl } from './controls/NumberControl';
import { StringControl } from './controls/StringControl';
import { ColorControl } from './controls/ColorControl';
import { SelectControl } from './controls/SelectControl';
import { Color } from '../../engine/types/Color';
import { Vector3 } from '../../engine/types/Vector3';
import { Quaternion } from '../../engine/types/Quaternion';
import { InterpolatedProperty } from '../../engine/properties/InterpolatedProperty';
import { UIUpdateManager } from '../UIUpdateManager';

// --- Vector3Control Component (Inlined) ---

interface InlineVector3ControlProps {
    label: string;
    value: Vector3;
    onChange: (newValue: Vector3) => void;
    id: string;
    property: SettingsProperty<any>;
}

const Vector3Control: React.FC<InlineVector3ControlProps> = React.memo(({ label, value, onChange, id, property }) => {
    const currentValueRef = React.useRef<HTMLDivElement>(null);
    const componentId = React.useRef(id + '-updater').current;
    const showCurrentValue = property instanceof InterpolatedProperty && property.showCurrentValueInUI;

    React.useEffect(() => {
        if (!showCurrentValue) {
            return;
        }

        const updateCallback = () => {
            if (!currentValueRef.current || !UIUpdateManager.instance) return;

            const propValue = property.value;
            let valueText = '';

            if (propValue instanceof Vector3) {
                valueText = `Current: (${propValue.x.toFixed(2)}, ${propValue.y.toFixed(2)}, ${propValue.z.toFixed(2)})`;
            } else if (propValue instanceof Quaternion) {
                const euler = propValue.toEulerAnglesDegrees();
                valueText = `Current: (${euler.x.toFixed(2)}, ${euler.y.toFixed(2)}, ${euler.z.toFixed(2)})`;
            }

            if (property instanceof InterpolatedProperty) {
                const isAnimating = property.isInterpolating ? 'Yes' : 'No';
                const duration = property.duration.toFixed(2);
                valueText += ` | Animating: ${isAnimating} | Duration: ${duration}s`;
            }
            
            currentValueRef.current.innerText = valueText;
        };
        
        // Register with the manager
        if (UIUpdateManager.instance) {
            UIUpdateManager.instance.register(componentId, updateCallback);
        }
        
        // Cleanup on unmount
        return () => {
            if (UIUpdateManager.instance) {
                UIUpdateManager.instance.unregister(componentId);
            }
        }
        
    }, [property, componentId, showCurrentValue]);

    const handleChange = (axis: 'x' | 'y' | 'z', val: string) => {
        const numValue = parseFloat(val);
        if (isNaN(numValue)) return;
        const newVector = value.clone();
        newVector[axis] = numValue;
        onChange(newVector);
    };

    const inputStyle: React.CSSProperties = { width: '55px', marginLeft: '5px', marginRight: '5px', backgroundColor: '#333', color: 'white', border: '1px solid #555', borderRadius: '4px', textAlign: 'center', padding: '4px' };
    const containerStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', marginBottom: '8px', fontFamily: 'sans-serif', fontSize: '14px' };
    const mainControlStyle: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center'};
    const labelStyle: React.CSSProperties = { flexShrink: 0, marginRight: '10px' };
    const inputsContainerStyle: React.CSSProperties = { display: 'flex', alignItems: 'center' };
    const axisLabelStyle: React.CSSProperties = { cursor: 'pointer', fontWeight: 'bold', color: '#999', fontSize: '12px' };
    const currentValueStyle: React.CSSProperties = {
        fontSize: '11px',
        color: '#aaa',
        textAlign: 'center',
        marginBottom: '4px',
        fontFamily: 'monospace',
        minHeight: '14px',
    };

    return (
        <div style={containerStyle} id={id}>
             {showCurrentValue && (
                <div ref={currentValueRef} style={currentValueStyle}>
                    {/* Content is now driven by the UIUpdateManager */}
                </div>
            )}
            <div style={mainControlStyle}>
            <label htmlFor={id} style={labelStyle}>{label}</label>
            <div style={inputsContainerStyle}>
                <label htmlFor={`${id}-x`} style={axisLabelStyle}>X</label>
                <input id={`${id}-x`} style={inputStyle} type="number" value={value.x.toFixed(2)} onChange={e => handleChange('x', e.target.value)} step="0.1" />
                <label htmlFor={`${id}-y`} style={axisLabelStyle}>Y</label>
                <input id={`${id}-y`} style={inputStyle} type="number" value={value.y.toFixed(2)} onChange={e => handleChange('y', e.target.value)} step="0.1" />
                <label htmlFor={`${id}-z`} style={axisLabelStyle}>Z</label>
                <input id={`${id}-z`} style={inputStyle} type="number" value={value.z.toFixed(2)} onChange={e => handleChange('z', e.target.value)} step="0.1" />
                </div>
            </div>
        </div>
    );
});


// --- Main PropertyControl Component ---

interface PropertyControlProps {
    property: SettingsProperty<any>;
    onPropertyChange: (property: SettingsProperty<any>, newValue: any) => void;
}

const getEditableValue = (p: SettingsProperty<any>) => {
    if (p instanceof InterpolatedProperty) {
        // For interpolated properties, the user edits the target value.
        return p.targetValue;
    }
    return p.value;
};

export const PropertyControl = (props: PropertyControlProps) => {
    const { property, onPropertyChange } = props;
    const [, forceUpdate] = React.useReducer(x => x + 1, 0);

    React.useEffect(() => {
        const handler = () => forceUpdate();
        property.on(handler);
        return () => property.off(handler);
    }, [property]);

    const { type, subtype, options, bounds } = property.typeInfo;

    const handleChange = React.useCallback((newValue: any) => {
        let finalValue = newValue;
        if (subtype === 'color' && typeof newValue === 'string') {
            finalValue = new Color(newValue);
        }
        
        onPropertyChange(property, finalValue);
    }, [property, onPropertyChange, subtype]);

    // Move conditional hooks to top level to avoid Rules of Hooks violation
    const handleQuaternionChange = React.useCallback((eulerAngles: Vector3) => {
        if (subtype === 'Quaternion') {
            const newQuaternion = Quaternion.fromEulerAnglesDegrees(eulerAngles);
            handleChange(newQuaternion);
        }
    }, [handleChange, subtype]);

    const eulerValue = React.useMemo(() => {
        if (subtype === 'Quaternion' && property.value instanceof Quaternion) {
            const editableValue = getEditableValue(property) as Quaternion | null;
            return editableValue?.toEulerAnglesDegrees() || Vector3.zero();
        }
        return Vector3.zero();
    }, [property.value, subtype]);

    if (property.value === undefined || property.value === null) {
        return (
             <div className="control unsupported-control">
                <label>{property.name}</label>
                <span>No value</span>
            </div>
        )
    }

    if (subtype === 'Vector3' && property.value instanceof Vector3) {
        const editableValue = getEditableValue(property) as Vector3 | null;
        if (!editableValue) return null;

        return (
            <Vector3Control
                id={property.id}
                label={property.name}
                value={editableValue}
                onChange={handleChange}
                property={property}
            />
        );
    }
    
    if (subtype === 'Quaternion' && property.value instanceof Quaternion) {
        const editableValue = getEditableValue(property) as Quaternion | null;
        if (!editableValue) return null;

        return (
            <Vector3Control
                id={property.id}
                label={property.name}
                value={eulerValue}
                onChange={handleQuaternionChange}
                property={property}
            />
        )
    }

    // Handle boolean properties first, even if they have options
    if (type === 'boolean') {
        return (
            <BooleanControl
                id={property.id}
                label={property.name}
                value={property.value}
                onChange={handleChange}
            />
        );
    }

    if (options && options.length > 0) {
        return (
            <SelectControl
                id={property.id}
                label={property.name}
                value={property.value}
                onChange={handleChange}
                options={options}
            />
        );
    }

    switch (type) {
        case 'number':
            return (
                <NumberControl
                    id={property.id}
                    label={property.name}
                    value={
                        (property instanceof InterpolatedProperty )
                        ? property.targetValue ?? 0
                        : property.value
                    }
                    onChange={handleChange}
                    min={bounds?.min}
                    max={bounds?.max}
                    step={bounds?.step}
                />
            );
        case 'string':
             if (subtype === 'color') {
                return (
                    <ColorControl
                        id={property.id}
                        label={property.name}
                        value={property.value instanceof Color ? property.value.toHex() : property.value}
                        onChange={handleChange}
                    />
                );
            }
            return (
                <StringControl
                    id={property.id}
                    label={property.name}
                    value={property.value}
                    onChange={handleChange}
                    multiline={property.typeInfo.multiline}
                />
            );

        default:
            return (
                <div className="control unsupported-control">
                    <label>{property.name}</label>
                    <span>{String(property.value)}</span>
                </div>
            );
    }
};